import { create } from 'zustand';
import { setToken, setRefreshToken, clearToken } from '../services/api';

async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

interface User {
  id: string;
  username: string;
  role: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadFromStorage: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,

  loadFromStorage: () => {
    try {
      const saved = localStorage.getItem('daily_user');
      if (saved) {
        const user = JSON.parse(saved);
        set({ user, isAuthenticated: true });
      }
    } catch { /* ignore */ }
  },

  login: async (username: string, password: string) => {
    const passwordHash = await sha256(password);
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password: passwordHash }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Ошибка входа');
    }

    const { accessToken, refreshToken, id, username: uname, role } = data.data;
    setToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);

    const user = { id, username: uname, role };
    localStorage.setItem('daily_user', JSON.stringify(user));
    set({ user, isAuthenticated: true });
  },

  logout: async () => {
    try {
      const rt = localStorage.getItem('daily_refresh_token');
      if (rt) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: rt }),
        });
      }
    } catch { /* ignore */ }
    clearToken();
    localStorage.removeItem('daily_user');
    set({ user: null, isAuthenticated: false });
  },
}));
